import React, { Component } from 'react';
import Monitor, { FingerPrint } from '../../../lib/monitoring/Monitor';
import { Image, ScrollView } from 'react-native';
import View from '../../../components/styled/View';
import { getValidTokenAsync } from '../../../api/spotifyLamdaRoutes';
import { UriInfo } from '../../../types/types';
import AudioProgressBar from '../AudioProgressBar';
import GalanoGrotesqueText from '../GalanoGrotesqueText';
import HeaderOne from '../HeaderOne';
import LinearGradientButton from '../LinearGradientButton';
import Link from '../Link';
import SplashScreen from '../SplashScreen';
import SpotifyInteractionPanel from '../SpotifyInteractionPanel';
import SpotifyPlayerError, { SpotifyErrorCodes } from '../SpotifyPlayerError';
import AppAnalytics, {
  GaCategories,
  GaActions,
} from '../../../lib/analytics/Analytics';
import styles from './styles';
import {
  DONE_TEXT,
  EMAIL_HELP_URL,
  EMAIL_HELP_LINK_TEXT,
  FINISHED_REVIEWING_TRACKS_TEXT,
  NON_TONEBIRDS_TRACK,
  TRACK_FINISHED_MESSAGE,
  WELCOME_MESSAGE_GENERIC,
  WELCOME_HELP_TEXT,
} from './constants';

type PlayerProps = {
  fanId: string;
  isReturningFan: boolean;
  feedbackIsComplete?: boolean;
  handleCurrentPlayerUri: (e: UriInfo) => void;
  navigation: any;
  trackList: any; // TODO: Update to the correct tpye of object
  updatePlayCount: () => void;
};

type PlayerState = {
  artistId: string;
  albumImage: string;
  currentTrackName: string;
  currentAlbum: string;
  currentArtist: string;
  currentPosition: number;
  duration: number;
  deviceId: string;
  error: string;
  firstListen: boolean;
  isSpotifyError: boolean;
  isToneBirdsTrack: boolean;
  numberOfTimesSendPlayCountCalled: number;
  noSessionsLeftToReview: boolean;
  playing: boolean;
  readyToSendPlayCount: boolean;
  spotifyPlayerErrorType: keyof typeof SpotifyErrorCodes;
  trackId: string;
  trackFinishedMessage: string;
  totalDuration: number;
  uriInfo: UriInfo;
};

class Player extends Component<PlayerProps, PlayerState> {
  private player;
  private playerCheckInterval;
  private positionIntervalId;

  constructor(props) {
    super(props);
    this.state = {
      albumImage: '',
      artistId: '',
      currentTrackName: '',
      currentAlbum: '',
      currentPosition: 0,
      currentArtist: '',
      deviceId: '',
      duration: 1,
      error: '',
      firstListen: true,
      isSpotifyError: false,
      isToneBirdsTrack: true,
      numberOfTimesSendPlayCountCalled: 0,
      noSessionsLeftToReview: false,
      playing: false,
      readyToSendPlayCount: false,
      spotifyPlayerErrorType: SpotifyErrorCodes.NONE,
      trackId: '',
      trackFinishedMessage: '',
      totalDuration: 0,
      uriInfo: { id: '', linkedFromId: '' },
    };
    // this will later be set by setInterval
    this.playerCheckInterval = null;
    this.positionIntervalId = null;
    this.player = null;
  }
  componentDidMount() {
    this.checkForPlayer();
    this.positionIntervalId = setInterval(this.songPositionTimer, 1000);
  }

  componentDidUpdate() {
    const {
      numberOfTimesSendPlayCountCalled,
      readyToSendPlayCount,
    } = this.state;
    const { updatePlayCount } = this.props;
    if (readyToSendPlayCount) {
      const setTimesPlayCountCalled = () => {
        if (numberOfTimesSendPlayCountCalled === 0) {
          updatePlayCount && updatePlayCount();
          this.setState({ numberOfTimesSendPlayCountCalled: 1 });
        }
      };
      this.setState({ readyToSendPlayCount: false }, () =>
        setTimesPlayCountCalled(),
      );
    }
  }

  componentWillUnmount() {
    clearInterval(this.playerCheckInterval);
    clearInterval(this.positionIntervalId);
    this.player.disconnect();
  }

  songPositionTimer = () => {
    const { isToneBirdsTrack } = this.state;
    if (this.player && this.state.playing) {
      this.player.getCurrentState().then((state) => {
        if (state !== null) {
          let { position, track_window } = state;
          const { current_track } = track_window;
          const { duration_ms: totalDuration } = current_track;
          let newPosition = position || 0;
          this.setState({ currentPosition: newPosition });
          const endOfSong =
            position > totalDuration - 2000 && position < totalDuration;
          if (endOfSong && isToneBirdsTrack) {
            this.onPlayClick();
            this.setState({ trackFinishedMessage: TRACK_FINISHED_MESSAGE });
          }
          const sendPlayCount = newPosition > 45000 && newPosition < 46000;
          if (sendPlayCount && isToneBirdsTrack) {
            this.setState({ readyToSendPlayCount: true });
          }
        }
      });
    }
  };

  // TODO: Refactor this eventually
  seedPlayer = ({
    spotify_uris,
    playerInstance: {
      _options: { getOAuthToken },
    },
  }) => {
    AppAnalytics.sendEvent(GaCategories.SPOTIFY, GaActions.LISTEN, 100);
    const { deviceId } = this.state;
    getOAuthToken((access_token) => {
      fetch(`https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`, {
        method: 'PUT',
        body: JSON.stringify({ uris: spotify_uris }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${access_token}`,
        },
      })
        .then((response) => {
          if (response.status >= 400) {
            throw new Error(
              `Failed to load resources: seed player function received a status of: ${response.status}`,
            );
          }
          this.player.togglePlay();
          this.setState({ firstListen: false });
        })
        .catch((e) => {
          const errorString = 'Error inside the seed player function';
          console.error(errorString, e);
          Monitor.notify(
            e,
            this.setMonitorError(errorString, 'seedPlayer function'),
          );
          this.setState({
            spotifyPlayerErrorType: SpotifyErrorCodes.INITIALIZATION,
            isSpotifyError: true,
          });
        });
    });
  };

  checkIfToneBirdTrack = (playerState) => {
    const { trackList } = this.props;
    let toneBirdTrack = true;
    const currentTrackId =
      playerState && playerState.track_window?.current_track?.id;
    const trackIds = trackList.map((track) => track.uri);
    const trackExists = trackIds.find((x) => x.includes(currentTrackId));
    if (!trackExists) {
      toneBirdTrack = false;
    }
    return toneBirdTrack;
  };

  onPlayerChange = (playerState) => {
    const { firstListen, isToneBirdsTrack } = this.state;

    // Handles the view state if user plays a non ToneBirds track
    if (!this.checkIfToneBirdTrack(playerState) && isToneBirdsTrack) {
      this.setState({ isToneBirdsTrack: false });
    }
    // If the user connects to Tonebirds app via Spotify and a tonebirds track is queued up hide the welcome screen
    if (firstListen === true) {
      this.setState({ firstListen: false });
    }

    if (playerState !== null) {
      const { duration, position, track_window } = playerState;
      const { current_track: currentTrack } = track_window;
      const currentTrackName = currentTrack.name;
      const trackId = currentTrack.id;

      const uriInfo = this.state.isSpotifyError
        ? { id: '', linkedFromId: '' }
        : {
            id: trackId,
            linkedFromId: currentTrack.linked_from?.id,
          };
      const artistId = currentTrack.artists[0].uri;
      const parsedArtistId = artistId.split(':')[2];
      const currentAlbum = currentTrack.album.name;
      const albumImage = currentTrack.album.images[0].url;
      const currentArtist = currentTrack.artists
        .map((artist) => artist.name)
        .join(', ');
      const playing = !playerState.paused;
      const totalDuration = currentTrack.duration_ms;
      const currentPosition = position || 0;
      this.props.handleCurrentPlayerUri(uriInfo);
      this.setState({
        artistId: parsedArtistId,
        albumImage,
        duration,
        currentTrackName,
        currentAlbum,
        currentArtist,
        currentPosition,
        playing,
        trackId,
        uriInfo,
        totalDuration,
      });
    } else {
      // state was null, user might have swapped to another device
      /* 
      This section handles Mobile phones. When you come back to the browser window, 
      this code sets it to the welcome screen. If this wasn't here you would not be able to press play
      on the player without refreshing the page
      */
      this.setState({ firstListen: true });
      const blankUriInfo = {
        id: '',
        linkedFromId: '',
      };
      this.props.handleCurrentPlayerUri(blankUriInfo);
      const errorString =
        'There is no spotify player state data available, maybe the device was swapped, user changed genres, or user changed tabs and device was disconnected';
      console.error(errorString);
    }
  };

  createEventHandlers = () => {
    // problem setting up the player
    this.player.on('initialization_error', (e) => {
      const errorString = 'Initialization error with the player';
      console.error(errorString, e);
      this.setState({
        spotifyPlayerErrorType: SpotifyErrorCodes.INITIALIZATION,
        isSpotifyError: true,
      });
      Monitor.notify(
        e,
        this.setMonitorError(errorString, 'Initialization error handler'),
      );
    });
    // problem authenticating the user.
    // either the token was invalid in the first place,
    // or it expired (it lasts one hour)
    this.player.on('authentication_error', async (e) => {
      // TODO: MORE WORK TO DO HERE. When this fires when a user is idel we need to handle it, ie refetch their token and reset the player to first listen and give a message of player timeout etc
      this.props.handleCurrentPlayerUri({ id: '', linkedFromId: '' }),
        this.setState({
          spotifyPlayerErrorType: SpotifyErrorCodes.PLAYER_AUTHENTICATION,
          isSpotifyError: true,
        });

      const errorString = 'Authentication error with the player';
      console.error(errorString, e);
      Monitor.notify(
        e,
        this.setMonitorError(errorString, 'Authentication error handler'),
      );
    });
    // currently only premium accounts can use the API
    this.player.on('account_error', (e) => {
      this.setState({
        spotifyPlayerErrorType: SpotifyErrorCodes.ACCOUNT_ERROR,
        isSpotifyError: true,
      });
      const errorString = 'Account error with the player';
      console.error(errorString, e);
      Monitor.notify(
        e,
        this.setMonitorError(errorString, 'Account error handler'),
      );
    });
    // loading/playing the track failed for some reason
    this.player.on('playback_error', (e) => {
      const errorString = 'Playback error with the player';
      console.error(errorString, e);
      Monitor.notify(
        e,
        this.setMonitorError(errorString, 'Playback error handler'),
      );
    });

    // Playback status updates
    this.player.on('player_state_changed', (playerState) => {
      this.onPlayerChange(playerState);
    });

    // Ready
    this.player.on('ready', (player) => {
      const { device_id: deviceId } = player;
      // This has to be here in order to update state and so it re renders with the new this.player
      this.setState({ deviceId });
    });
  };
  checkForPlayer = async () => {
    // if the Spotify SDK has loaded
    if (window.Spotify !== null) {
      // cancel the interval
      // clearInterval(this.playerCheckInterval);
      // create a new player
      this.player = new window.Spotify.Player({
        name: 'ToneBirds Music App',
        getOAuthToken: async (cb) => {
          const token = await getValidTokenAsync();
          cb(token);
        },
        volume: 0.5,
      });
      // set up the player's event handlers
      this.createEventHandlers();

      // finally, connect!
      this.player.connect().then((success) => {
        if (success) {
          console.log(
            'The Web Playback SDK successfully connected to Spotify!',
          );
        }
      });
    }
  };

  handleSpotifyClearError = () => {
    const { navigation } = this.props;
    navigation.replace('Fan', { screen: 'Listen' });
    this.setState({ isToneBirdsTrack: true, isSpotifyError: false });
  };

  onPrevClick = () => {
    this.player.previousTrack();
  };

  onPlayClick = () => {
    this.player.togglePlay();
    const playOrPause = this.state.playing ? GaActions.PAUSED : GaActions.PLAY; // This is correct order to log the appropriate action (play or pause)
    AppAnalytics.sendEvent(GaCategories.SPOTIFY, playOrPause, 120);
  };

  onNextClick = async () => {
    const { trackId, uriInfo } = this.state;
    const { handleCurrentPlayerUri, trackList } = this.props;
    const listOfTrackIds = trackList.map((track) => track.uri);
    const lastId = listOfTrackIds[listOfTrackIds.length - 1];
    const formatedLastId = lastId.split(':')[2];
    const isLastTrack =
      formatedLastId == trackId || formatedLastId == uriInfo.linkedFromId;

    if (isLastTrack) {
      await this.player.disconnect();
      handleCurrentPlayerUri({ id: '', linkedFromId: '' });
      this.setState({
        noSessionsLeftToReview: true,
        numberOfTimesSendPlayCountCalled: 0,
        trackFinishedMessage: '',
      });
      AppAnalytics.sendEvent(GaCategories.SPOTIFY, GaActions.FINISH, 140);
    } else {
      this.setState({
        numberOfTimesSendPlayCountCalled: 0,
        trackFinishedMessage: '',
      });
      this.player.nextTrack();
      AppAnalytics.sendEvent(GaCategories.SPOTIFY, GaActions.NEXT, 130);
    }
  };

  restartTrack = () => {
    this.setState({ numberOfTimesSendPlayCountCalled: 0 });
    this.player.seek(500);
  };

  setMonitorError = (message: string, name) => {
    const { fanId } = this.props;
    return {
      context: { user_id: fanId, user_type: 'fan' },
      name: `SpotifyPlayer-${name}`,
      fingerprint: FingerPrint.fanListen,
      message,
    };
  };

  render() {
    const {
      currentArtist,
      currentTrackName,
      currentPosition,
      firstListen,
      isSpotifyError,
      isToneBirdsTrack,
      noSessionsLeftToReview,
      playing,
      spotifyPlayerErrorType,
      trackId,
      trackFinishedMessage,
      totalDuration,
    } = this.state;
    const { feedbackIsComplete, trackList, isReturningFan } = this.props;
    // TODO: Memoize this
    const trackIds = trackList.map((track) => track.uri);
    if (!this.player) {
      return <SplashScreen text={'Loading your player experience..'} />;
    }

    if (noSessionsLeftToReview) {
      return (
        <ScrollView contentContainerStyle={styles.contentContainer}>
          <View style={styles.container}>
            <HeaderOne title={DONE_TEXT} />
            <GalanoGrotesqueText style={styles.platformText}>
              {FINISHED_REVIEWING_TRACKS_TEXT}
            </GalanoGrotesqueText>
          </View>
        </ScrollView>
      );
    }

    if (isSpotifyError) {
      return (
        <SpotifyPlayerError
          errorType={spotifyPlayerErrorType}
          handleSpotifyErrorReset={this.handleSpotifyClearError}
        />
      );
    }

    const skipTrackEnabled = feedbackIsComplete && currentPosition > 45000;
    const welcomeText = isReturningFan
      ? 'Welcome Back! '
      : 'Welcome To ToneBirds ';
    return (
      <View>
        <View style={styles.container}>
          {firstListen && (
            <View style={styles.welcomeContainer}>
              <HeaderOne style={styles.welcomeBack} title={welcomeText} />
              <GalanoGrotesqueText style={styles.platformText}>
                {WELCOME_MESSAGE_GENERIC}
              </GalanoGrotesqueText>
              <LinearGradientButton
                title="Listen now"
                onPress={() =>
                  this.seedPlayer({
                    playerInstance: this.player,
                    spotify_uris: trackIds,
                  })
                }
              />
              <View style={styles.helpContainer}>
                <GalanoGrotesqueText style={styles.helpText}>
                  {WELCOME_HELP_TEXT}
                  <Link
                    onPress={() => {}}
                    url={EMAIL_HELP_URL}
                    text={EMAIL_HELP_LINK_TEXT}
                  />
                </GalanoGrotesqueText>
              </View>
            </View>
          )}
          {!firstListen && (
            <View>
              <View style={styles.imageContainer}>
                <Image
                  style={styles.image}
                  source={{ uri: `${this.state.albumImage}` }}
                />
              </View>
              <View style={styles.trackInfoContainer}>
                <GalanoGrotesqueText style={styles.trackName}>
                  {currentTrackName}
                </GalanoGrotesqueText>
                <GalanoGrotesqueText style={styles.artistName}>
                  {currentArtist}
                </GalanoGrotesqueText>
              </View>
              <AudioProgressBar
                currentPosition={currentPosition}
                totalDuration={totalDuration}
              />
              <View style={styles.contentContainer}>
                {!!trackId && (
                  <SpotifyInteractionPanel
                    isPlaying={playing}
                    onNextClick={this.onNextClick}
                    onPlayClick={this.onPlayClick}
                    restartTrack={this.restartTrack}
                    skipTrackEnabled={!!skipTrackEnabled}
                  />
                )}
              </View>
              <GalanoGrotesqueText style={styles.trackFinishedText}>
                {trackFinishedMessage}
              </GalanoGrotesqueText>
              {/* If a user ever plays a track from Spotify that is not a Tonebird track display this */}
              {!isToneBirdsTrack && (
                <View style={styles.nonToneBirdsTracks}>
                  <GalanoGrotesqueText style={styles.trackFinishedText}>
                    {NON_TONEBIRDS_TRACK}
                  </GalanoGrotesqueText>
                  <LinearGradientButton
                    title="Refresh"
                    onPress={this.handleSpotifyClearError}
                  />
                </View>
              )}
            </View>
          )}
        </View>
      </View>
    );
  }
}

export default Player;
